<template>
  <div>
    <!-- SCOPE, RENDERED. One card per zone requirement, plus Unzoned. The rows
         are the customer pill's rows — the same set the stats row above reads —
         and nothing else on the board narrows this tab, which is why the
         filter controls are hidden here. Clicking a card reproduces the
         card's count on the Requirements tab: every other filter is cleared
         and Zone is set, so the number you clicked is the number you land on. -->
    <p class="text-xs text-gray-600 mb-4 leading-relaxed">
      A zone is the parent requirement a ticket belongs to, one step above an
      epic. Every row below is counted under the zone reached by walking its
      parent chain; a row with no zone ancestor is Unzoned. Hades parents a new
      ticket under a zone the night it investigates it; an existing parent is
      never overridden.
    </p>

    <p v-if="!zones.length" class="text-sm text-gray-600 py-4">
      No zones exist yet. Create a requirement of type Zone on the Work tab and
      parent epics under it.
    </p>

    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <button
        v-for="card in cards"
        :key="card.key"
        type="button"
        class="text-left border rounded-lg p-4 bg-white hover:border-primary-500"
        :class="card.unzoned ? 'border-dashed border-gray-300' : 'border-gray-200'"
        @click="$emit('select', card.name)"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div
              class="text-sm font-semibold leading-snug truncate"
              :class="card.unzoned ? 'text-gray-600 italic' : 'text-gray-900'"
              :title="card.name"
            >{{ card.name }}</div>
            <div class="text-[11px] text-gray-600 mt-0.5">
              <template v-if="card.unzoned">No zone ancestor</template>
              <template v-else-if="card.status && card.status !== 'approved'">
                {{ statusLabel(card.status) }}
              </template>
            </div>
          </div>
          <div class="text-[22px] font-semibold text-gray-900 leading-none flex-none">
            {{ card.total }}
          </div>
        </div>

        <!-- THE TEAM. A zone has no owner; it has people per concern, from the
             Zone Model sheet, held on the zone requirement's metadata. -->
        <div v-if="card.team.length" class="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-2 text-[11px]">
          <div v-for="slot in card.team" :key="slot.label" class="flex justify-between gap-2 min-w-0">
            <span class="text-gray-500 flex-none">{{ slot.label }}</span>
            <span class="text-gray-800 truncate" :title="slot.names">{{ slot.names }}</span>
          </div>
        </div>

        <!-- WHAT DEMANDS ATTENTION, same vocabulary as the stats row and the
             rail: a zone card is that row for one zone. -->
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
          <div v-for="stat in card.stats" :key="stat.label" class="flex justify-between">
            <span class="text-gray-600">{{ stat.label }}</span>
            <span :class="stat.value && stat.alarm ? 'text-red-dark font-semibold' : 'text-gray-800 font-medium'">
              {{ stat.value }}
            </span>
          </div>
        </div>

        <div class="flex flex-wrap gap-1.5 mt-3">
          <span
            v-for="chip in card.types"
            :key="chip.name"
            class="px-1.5 py-0.5 rounded text-[11px] font-medium leading-tight"
            :class="typeClass(chip.name)"
          >{{ chip.name }} {{ chip.count }}</span>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import {
  STATUS_LABELS, TYPE_CLASSES, daysUntil,
} from '../support_constants';
import { label } from './hades_constants';
import { UNZONED, quickFilterAdmits } from './hades_filters';

// The six concern rows of the Zone Model sheet, in the order it lists them.
// A concern missing from a zone's team is simply not shown.
const TEAM_CONCERNS = [
  ['impact', 'Impact'],
  ['ux', 'UX'],
  ['bugs', 'Bugs'],
  ['features', 'Features'],
  ['nps', 'NPS'],
  ['architecture', 'Architecture'],
];

function teamSlots(team) {
  return TEAM_CONCERNS
    .filter(([key]) => (team?.[key] || []).length)
    .map(([key, label]) => ({ label, names: team[key].join(', ') }));
}

export default {
  name: 'HadesZones',
  props: {
    // The board's customer-scoped rows (Support_Hades.customerTickets).
    tickets: { type: Array, required: true },
    // The zone requirements from the portfolio payload: {id, title, status,
    // team}. Listed even at zero rows — an empty zone is a real answer.
    zones: { type: Array, required: true },
  },
  emits: ['select'],
  computed: {
    cards() {
      const byZone = new Map();
      for (const zone of this.zones) byZone.set(zone.title, []);
      const unzoned = [];
      for (const row of this.tickets) {
        const name = row.zone_name;
        if (name && byZone.has(name)) byZone.get(name).push(row);
        else if (name) byZone.set(name, [row]);
        else unzoned.push(row);
      }
      const cards = this.zones.map((zone) => ({
        key: `zone:${zone.id}`,
        name: zone.title,
        team: teamSlots(zone.team),
        status: zone.status,
        unzoned: false,
        ...this.summarise(byZone.get(zone.title) || []),
      }));
      // Rows whose zone ancestor is not in the payload's zone list cannot
      // happen today (both come from the same query), but a card is cheaper
      // than a silently dropped row if it ever does.
      for (const [name, rows] of byZone) {
        if (!this.zones.some((z) => z.title === name)) {
          cards.push({ key: `zone:${name}`, name, team: [], status: null,
            unzoned: false, ...this.summarise(rows) });
        }
      }
      cards.push({ key: 'unzoned', name: UNZONED, team: [], status: null,
        unzoned: true, ...this.summarise(unzoned) });
      return cards;
    },
  },
  methods: {
    summarise(rows) {
      const inQueue = (queue) => rows.filter((r) => (r.queues || []).includes(queue)).length;
      const typeCounts = new Map();
      for (const row of rows) {
        const name = row.requirement_type || '—';
        typeCounts.set(name, (typeCounts.get(name) || 0) + 1);
      }
      return {
        total: rows.length,
        stats: [
          { label: 'Not investigated', value: rows.filter((r) => !r.investigated).length },
          { label: 'Unnoticed', value: inQueue('unnoticed'), alarm: true },
          { label: 'Needs reply', value: rows.filter((r) => quickFilterAdmits(r, 'needs_reply')).length, alarm: true },
          { label: 'Overdue', value: rows.filter((r) => daysUntil(r.requirement_due_date) < 0).length, alarm: true },
          { label: 'Ready to close', value: inQueue('shipped_record_stale') },
          { label: 'Unowned', value: rows.filter((r) => !r.owner_name).length },
        ],
        types: [...typeCounts.entries()]
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .map(([name, count]) => ({ name, count })),
      };
    },
    statusLabel(status) {
      return label(STATUS_LABELS, status);
    },
    typeClass(name) {
      return TYPE_CLASSES[name] || 'bg-gray-100 text-gray-600';
    },
  },
};
</script>
